import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Laptop, Moon, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSearch } from '@/context/search-provider'
import { useTheme } from '@/context/theme-provider'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { ScrollArea } from '@/components/ui/scroll-area'

export function ThreePlCommandMenu() {
  const navigate = useNavigate()
  const { t } = useTranslation('three-pl-shell')
  const { setTheme } = useTheme()
  const { open, setOpen } = useSearch()

  const navGroups = useMemo(
    () => [
      {
        title: t('sidebar.group.catalog'),
        items: [
          {
            title: t('nav.products'),
            url: '/products' as const,
          },
        ],
      },
    ],
    [t]
  )

  const runCommand = React.useCallback(
    (command: () => unknown) => {
      setOpen(false)
      command()
    },
    [setOpen]
  )

  return (
    <CommandDialog
      modal
      open={open}
      onOpenChange={setOpen}
      title={t('commandMenu.dialogTitle')}
      description={t('commandMenu.dialogDescription')}
    >
      <CommandInput placeholder={t('commandMenu.inputPlaceholder')} />
      <CommandList>
        <ScrollArea type="hover" className="h-72 pe-1">
          <CommandEmpty>{t('commandMenu.noResults')}</CommandEmpty>
          {navGroups.map((group) => (
            <CommandGroup key={group.title} heading={group.title}>
              {group.items.map((navItem) => (
                <CommandItem
                  key={navItem.url}
                  value={navItem.title}
                  onSelect={() => {
                    runCommand(() => navigate(navItem.url))
                  }}
                >
                  <div className="flex size-4 items-center justify-center">
                    <ArrowRight className="size-2 text-muted-foreground/80" />
                  </div>
                  {navItem.title}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
          <CommandSeparator />
          <CommandGroup heading={t('commandMenu.themeHeading')}>
            <CommandItem onSelect={() => runCommand(() => setTheme('light'))}>
              <Sun /> <span>Light</span>
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => setTheme('dark'))}>
              <Moon className="scale-90" />
              <span>Dark</span>
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => setTheme('system'))}>
              <Laptop />
              <span>System</span>
            </CommandItem>
          </CommandGroup>
        </ScrollArea>
      </CommandList>
    </CommandDialog>
  )
}
